import { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/DatePicker";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { api, resolveArtworkUrl, COLOURS, AVAILABILITIES, COLOUR_SWATCHES } from "@/lib/api";
import { Upload, Link as LinkIcon, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import DiscogsSearchDialog from "@/components/DiscogsSearchDialog";

const CONDITIONS = ["Poor", "Good", "Excellent", "Near Mint", "Mint"];
const LOCATIONS = ["Ruth", "Andy", "Archive", "Lagom"];
const SPEEDS = ["33", "45", "78"];

const blank = {
    artist: "", album_title: "", speed: "33",
    media_condition: "Near Mint", sleeve_condition: "Near Mint",
    location: "Andy", release_year: "", label: "", catalog_number: "",
    genre: "", notes: "", purchase_price: "", purchase_date: "",
    date_last_cleaned: "", artwork_url: "",
    discogs_id: null, discogs_url: "",
    colour: "Black", limited: false, availability: "Common",
};

export default function VinylDialog({ open, onOpenChange, onSave, initial }) {
    const [form, setForm] = useState(blank);
    const [artMode, setArtMode] = useState("upload"); // upload | url
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [discogsOpen, setDiscogsOpen] = useState(false);

    const handleDiscogsPick = (d) => {
        setForm(f => ({
            ...f,
            artist: d.artist || f.artist,
            album_title: d.album_title || f.album_title,
            release_year: d.release_year ? String(d.release_year) : f.release_year,
            label: d.label || f.label,
            catalog_number: d.catalog_number || f.catalog_number,
            genre: d.genre || f.genre,
            speed: d.speed || f.speed,
            artwork_url: d.cover_image || f.artwork_url,
            discogs_id: d.discogs_id || f.discogs_id,
            discogs_url: d.discogs_url || f.discogs_url,
        }));
        if (d.cover_image) setArtMode("url");
    };

    useEffect(() => {
        if (open) {
            if (initial) {
                setForm({
                    ...blank,
                    ...initial,
                    release_year: initial.release_year ?? "",
                    purchase_price: initial.purchase_price ?? "",
                    colour: initial.colour ?? "Black",
                    limited: !!initial.limited,
                    availability: initial.availability ?? "Common",
                });
                setArtMode(initial.artwork_url?.startsWith("http") ? "url" : "upload");
            } else {
                setForm(blank);
                setArtMode("upload");
            }
        }
    }, [open, initial]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            set("artwork_url", data.url);
            toast.success("Artwork uploaded");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async () => {
        if (!form.artist.trim() || !form.album_title.trim()) {
            toast.error("Artist and album title are required");
            return;
        }
        setSaving(true);
        const payload = {
            ...form,
            release_year: form.release_year ? parseInt(form.release_year, 10) : null,
            purchase_price: form.purchase_price !== "" ? parseFloat(form.purchase_price) : null,
            purchase_date: form.purchase_date || null,
            date_last_cleaned: form.date_last_cleaned || null,
            artwork_url: form.artwork_url || null,
            label: form.label || null,
            catalog_number: form.catalog_number || null,
            genre: form.genre || null,
            notes: form.notes || null,
            discogs_id: form.discogs_id || null,
            discogs_url: form.discogs_url || null,
            colour: form.colour || "Black",
            limited: !!form.limited,
            availability: form.availability || "Common",
        };
        // remove id/created_at/updated_at fields if present from initial
        ["id", "created_at", "updated_at", "artwork_storage_path"].forEach(k => delete payload[k]);
        await onSave(payload, initial?.id);
        setSaving(false);
    };

    const previewSrc = form.artwork_url ? resolveArtworkUrl({ artwork_url: form.artwork_url }) : null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card" data-testid="vinyl-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-3xl font-bold tracking-tight">
                        {initial ? "Edit record" : "Add a new record"}
                    </DialogTitle>
                    <DialogDescription>
                        Capture every detail — from sleeve scuffs to label imprint.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center justify-between gap-3 p-3 rounded-md bg-secondary/60 border border-border">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <Sparkles className="w-4 h-4 text-primary shrink-0" strokeWidth={1.5} />
                        <p className="text-sm text-foreground/80 truncate">
                            Save time — search Discogs to auto-fill artist, album, year, label & artwork.
                        </p>
                    </div>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setDiscogsOpen(true)}
                        className="shrink-0 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary"
                        data-testid="open-discogs-search-btn"
                    >
                        Search Discogs
                    </Button>
                </div>

                <div className="grid md:grid-cols-2 gap-6 py-2">
                    {/* Artwork */}
                    <div className="space-y-3">
                        <Label className="overline">Artwork</Label>
                        <div className="aspect-square rounded-md border border-border bg-secondary overflow-hidden flex items-center justify-center">
                            {previewSrc ? (
                                <img src={previewSrc} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <div className="text-center text-muted-foreground text-sm p-6">
                                    No artwork yet
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant={artMode === "upload" ? "default" : "outline"}
                                size="sm"
                                onClick={() => setArtMode("upload")}
                                className="flex-1"
                                data-testid="art-mode-upload"
                            >
                                <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload
                            </Button>
                            <Button
                                type="button"
                                variant={artMode === "url" ? "default" : "outline"}
                                size="sm"
                                onClick={() => setArtMode("url")}
                                className="flex-1"
                                data-testid="art-mode-url"
                            >
                                <LinkIcon className="w-3.5 h-3.5 mr-1.5" /> URL
                            </Button>
                        </div>
                        {artMode === "upload" ? (
                            <label className="block">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => handleUpload(e.target.files?.[0])}
                                    className="hidden"
                                    data-testid="art-file-input"
                                />
                                <span className="block text-center w-full py-2 px-3 text-sm rounded-md border border-dashed border-border hover:border-primary hover:bg-primary/5 cursor-pointer transition-colors">
                                    {uploading ? (
                                        <span className="inline-flex items-center gap-2">
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…
                                        </span>
                                    ) : "Choose image file"}
                                </span>
                            </label>
                        ) : (
                            <Input
                                value={form.artwork_url || ""}
                                onChange={(e) => set("artwork_url", e.target.value)}
                                placeholder="https://example.com/cover.jpg"
                                data-testid="art-url-input"
                            />
                        )}
                    </div>

                    {/* Fields */}
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="artist" className="overline">Artist *</Label>
                            <Input id="artist" value={form.artist} onChange={(e) => set("artist", e.target.value)} className="mt-1.5" data-testid="input-artist" />
                        </div>
                        <div>
                            <Label htmlFor="album_title" className="overline">Album title *</Label>
                            <Input id="album_title" value={form.album_title} onChange={(e) => set("album_title", e.target.value)} className="mt-1.5" data-testid="input-album-title" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="overline">Location</Label>
                                <Select value={form.location} onValueChange={(v) => set("location", v)}>
                                    <SelectTrigger className="mt-1.5" data-testid="input-location"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {LOCATIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="overline">Speed</Label>
                                <Select value={form.speed} onValueChange={(v) => set("speed", v)}>
                                    <SelectTrigger className="mt-1.5" data-testid="input-speed"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {SPEEDS.map(s => <SelectItem key={s} value={s}>{s} rpm</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="overline">Media condition</Label>
                                <Select value={form.media_condition} onValueChange={(v) => set("media_condition", v)}>
                                    <SelectTrigger className="mt-1.5" data-testid="input-media"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="overline">Sleeve condition</Label>
                                <Select value={form.sleeve_condition} onValueChange={(v) => set("sleeve_condition", v)}>
                                    <SelectTrigger className="mt-1.5" data-testid="input-sleeve"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="overline">Colour</Label>
                                <Select value={form.colour} onValueChange={(v) => set("colour", v)}>
                                    <SelectTrigger className="mt-1.5" data-testid="input-colour"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {COLOURS.map(c => (
                                            <SelectItem key={c} value={c}>
                                                <span className="inline-flex items-center gap-2">
                                                    <span
                                                        className="inline-block w-3 h-3 rounded-full border border-border"
                                                        style={{ background: COLOUR_SWATCHES[c] || "#111" }}
                                                    />
                                                    {c}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="overline">Availability</Label>
                                <Select value={form.availability} onValueChange={(v) => set("availability", v)}>
                                    <SelectTrigger className="mt-1.5" data-testid="input-availability"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {AVAILABILITIES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="flex items-center justify-between rounded-md border border-border bg-secondary/40 px-3 py-2.5">
                            <div>
                                <Label className="overline">Limited edition</Label>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {form.limited ? "Marked as limited" : "Standard pressing"}
                                </p>
                            </div>
                            <Switch
                                checked={!!form.limited}
                                onCheckedChange={(v) => set("limited", v)}
                                className={form.limited ? "data-[state=checked]:bg-[#C0392B]" : ""}
                                data-testid="input-limited"
                            />
                        </div>
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4 pt-2">
                    <div>
                        <Label className="overline">Release year</Label>
                        <Input type="number" value={form.release_year} onChange={(e) => set("release_year", e.target.value)} className="mt-1.5" data-testid="input-year" />
                    </div>
                    <div>
                        <Label className="overline">Label</Label>
                        <Input value={form.label} onChange={(e) => set("label", e.target.value)} className="mt-1.5" data-testid="input-label" />
                    </div>
                    <div>
                        <Label className="overline">Catalog #</Label>
                        <Input value={form.catalog_number} onChange={(e) => set("catalog_number", e.target.value)} className="mt-1.5" data-testid="input-catalog" />
                    </div>
                    <div>
                        <Label className="overline">Genre</Label>
                        <Input value={form.genre} onChange={(e) => set("genre", e.target.value)} className="mt-1.5" data-testid="input-genre" />
                    </div>
                    <div>
                        <Label className="overline">Purchase price</Label>
                        <Input type="number" step="0.01" value={form.purchase_price} onChange={(e) => set("purchase_price", e.target.value)} className="mt-1.5" data-testid="input-price" />
                    </div>
                    <div>
                        <Label className="overline">Purchase date</Label>
                        <div className="mt-1.5">
                            <DatePicker
                                value={form.purchase_date || ""}
                                onChange={(v) => set("purchase_date", v)}
                                placeholder="Pick a date"
                                testId="input-purchase-date"
                            />
                        </div>
                    </div>
                    <div className="md:col-span-3">
                        <Label className="overline">Date last cleaned</Label>
                        <div className="mt-1.5 max-w-xs">
                            <DatePicker
                                value={form.date_last_cleaned || ""}
                                onChange={(v) => set("date_last_cleaned", v)}
                                placeholder="Never cleaned"
                                testId="input-cleaned"
                            />
                        </div>
                    </div>
                    <div className="md:col-span-3">
                        <Label className="overline">Notes</Label>
                        <Textarea
                            value={form.notes}
                            onChange={(e) => set("notes", e.target.value)}
                            placeholder="Pressing details, condition notes, memories…"
                            rows={3}
                            className="mt-1.5"
                            data-testid="input-notes"
                        />
                    </div>
                </div>

                <DialogFooter className="pt-4">
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cancel-btn">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={saving}
                        className="bg-primary hover:bg-primary/90 text-primary-foreground"
                        data-testid="save-record-btn"
                    >
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {initial ? "Save changes" : "Add to collection"}
                    </Button>
                </DialogFooter>
            </DialogContent>
            <DiscogsSearchDialog
                open={discogsOpen}
                onOpenChange={setDiscogsOpen}
                onPick={handleDiscogsPick}
            />
        </Dialog>
    );
}
